import { useSignIn as useClerkSignIn } from "@clerk/clerk-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getClerkErrorMessage } from "@/utils/clerk-helper";

type SignInStep = "email" | "verifying";

interface SignInFlowState {
  step: SignInStep;
  email: string;
  code: string;
  isLoading: boolean;
  error: string | null;
}

/**
 * useSignInFlow — wraps Clerk's useSignIn() to drive the two-step passwordless
 * OTP sign-in flow shown in the design demo:
 *
 *   Step 1 (email):     User enters email → we call signIn.create() with
 *                        strategy 'email_code' to send a one-time code.
 *   Step 2 (verifying): User enters the code → we call attemptFirstFactor()
 *                        to verify it, then activate the session.
 */
export function useSignInFlow() {
  const { signIn, setActive, isLoaded } = useClerkSignIn();

  const [state, setState] = useState<SignInFlowState>({
    step: "email",
    email: "",
    code: "",
    isLoading: false,
    error: null,
  });

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email, error: null }));
  }, []);

  const setCode = useCallback((code: string) => {
    setState((prev) => ({ ...prev, code, error: null }));
  }, []);

  const dispatchOtp = useCallback(
    async (emailAddress: string) => {
      await signIn!.create({
        identifier: emailAddress,
        strategy: "email_code",
      });
    },
    [signIn],
  );

  /**
   * Step 1 → Step 2: create the sign-in and send the OTP email.
   */
  const sendCode = useCallback(async () => {
    if (!isLoaded || !signIn) return;
    if (stateRef.current.isLoading) return;

    const trimmedEmail = stateRef.current.email.trim();
    if (!trimmedEmail) {
      setState((prev) => ({ ...prev, error: "Please enter your email address." }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await dispatchOtp(trimmedEmail);
      setState((prev) => ({ ...prev, step: "verifying", isLoading: false }));
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false, error: getClerkErrorMessage(e) }));
    }
  }, [isLoaded, signIn, dispatchOtp]);

  /**
   * Step 2 → done: verify the OTP code and activate the session.
   * Returns true on success so the caller can handle navigation.
   */
  const verifyCode = useCallback(async (): Promise<boolean> => {
    if (!isLoaded || !signIn || !setActive) return false;
    if (stateRef.current.isLoading) return false;

    const trimmedCode = stateRef.current.code.trim();
    if (!trimmedCode) {
      setState((prev) => ({ ...prev, error: "Please enter the verification code." }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code: trimmedCode,
      });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        return true;
      }

      // Shouldn't happen with email_code, but handle gracefully.
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Verification incomplete. Please try again.",
      }));
      return false;
    } catch (e) {
      const message = getClerkErrorMessage(e, "Invalid verification code. Please try again.");
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      return false;
    }
  }, [isLoaded, signIn, setActive]);

  /**
   * Resend the OTP code (same as sendCode but doesn't change step).
   */
  const resendCode = useCallback(async () => {
    if (!isLoaded || !signIn) return;
    if (stateRef.current.isLoading) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await dispatchOtp(stateRef.current.email.trim());
      setState((prev) => ({ ...prev, isLoading: false }));
    } catch (e) {
      const message = getClerkErrorMessage(e, "Failed to resend code. Please try again.");
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, [isLoaded, signIn, dispatchOtp]);

  /** Go back from code step to email step. */
  const goBack = useCallback(() => {
    setState((prev) => ({ ...prev, step: "email", code: "", error: null }));
  }, []);

  return {
    ...state,
    isReady: isLoaded,
    setEmail,
    setCode,
    sendCode,
    verifyCode,
    resendCode,
    goBack,
  };
}
